import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export interface ImageLinkCard {
  /** Link target for the card. */
  path: string;
  icon: LucideIcon;
  title: string;
  description: string;
  /** Background image, shared with the linked page's hero. */
  image: string;
  imageAlt: string;
}

/**
 * Shared grid of image-backed link cards, used for both services and sectors so
 * the two stay in sync. Each card uses its image as a background with a dark
 * navy overlay for text legibility, and links through to a detail page that
 * reuses the same image as its hero.
 */
export default function ImageCardGrid({ cards }: { cards: ImageLinkCard[] }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {cards.map(card => (
        <Link
          key={card.path}
          to={card.path}
          className="group relative flex flex-col justify-end overflow-hidden rounded-2xl border border-border min-h-[300px] p-8 shadow-sm hover:shadow-xl transition-all duration-300"
        >
          {/* Background image */}
          <img
            src={card.image}
            alt={card.imageAlt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/85 to-primary/50" />

          {/* Content */}
          <div className="relative">
            <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
              <card.icon className="h-6 w-6 text-accent-foreground" />
            </div>
            <h3 className="text-xl font-heading font-bold text-white mb-3">{card.title}</h3>
            <p className="text-white/80 text-sm leading-relaxed mb-4">{card.description}</p>
            <span className="inline-flex items-center text-sm font-medium text-teal">
              Learn more
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}
